<script setup lang="ts">
import { computed, ref } from "vue";
import { storeToRefs } from "pinia";
import { useQuery, useQueryClient } from "@tanstack/vue-query";

import { importLocations, listLocations } from "@/api/client";
import PageLayout from "@/components/PageLayout.vue";
import { useProjectStore } from "@/stores/project";

const queryClient = useQueryClient();
const { selectedProjectId } = storeToRefs(useProjectStore());

const { data: locations, isPending, isError, error } = useQuery({
  queryKey: ["locations", selectedProjectId],
  queryFn: () => listLocations(selectedProjectId.value),
});

const search = ref("");
const filtered = computed(() => {
  const q = search.value.trim().toLowerCase();
  return (locations.value ?? []).filter((l) => {
    if (!q) return true;
    return (
      l.ref.toLowerCase().includes(q) ||
      (l.name ?? "").toLowerCase().includes(q) ||
      (l.area ?? "").toLowerCase().includes(q) ||
      (l.structure_type ?? "").toLowerCase().includes(q)
    );
  });
});

const importing = ref(false);
const importMessage = ref<string | null>(null);
const importError = ref<string | null>(null);

async function onFile(event: Event): Promise<void> {
  const input = event.target as HTMLInputElement;
  const file = input.files?.[0];
  if (!file || !selectedProjectId.value) return;
  importing.value = true;
  importMessage.value = null;
  importError.value = null;
  try {
    const result = await importLocations(selectedProjectId.value, file);
    importMessage.value = `Imported ${result.locations_imported} structures.`;
    await queryClient.invalidateQueries({ queryKey: ["locations"] });
  } catch (e) {
    importError.value = e instanceof Error ? e.message : String(e);
  } finally {
    importing.value = false;
    input.value = "";
  }
}
</script>

<template>
  <PageLayout>
    <div class="grid gap-8">
      <header>
        <h1 class="text-2xl font-extrabold text-neutral-900">
          Structures
        </h1>
        <p class="mt-1 text-neutral-500">
          The locations (poles, cantilevers, MPAs) for the current project —
          what quality check-sheets and defects attach to.
        </p>
      </header>

      <section class="rounded-xl border border-neutral-200 bg-neutral-0 p-6">
        <h2 class="mb-2 font-semibold">
          Import structures
        </h2>
        <p class="mb-4 text-sm text-neutral-500">
          Upload the project's location schedule (<code>.xlsx</code>). Columns
          are matched by header (structure ref, description, type, area).
          Re-importing is safe — structures are matched on their reference.
        </p>
        <label
          v-if="selectedProjectId"
          class="inline-flex cursor-pointer items-center rounded-md bg-brand-primary px-4 py-2 font-semibold text-brand-primary-on transition hover:opacity-90"
          :class="{ 'pointer-events-none opacity-50': importing }"
        >
          {{ importing ? "Importing…" : "Choose workbook…" }}
          <input
            type="file"
            accept=".xlsx,.xlsm,.csv"
            class="hidden"
            :disabled="importing"
            @change="onFile"
          >
        </label>
        <p
          v-else
          class="text-sm text-neutral-400"
        >
          Select a project first.
        </p>
        <p
          v-if="importMessage"
          class="mt-3 text-sm text-semantic-success"
        >
          {{ importMessage }}
        </p>
        <p
          v-if="importError"
          class="mt-3 text-sm text-semantic-danger"
        >
          {{ importError }}
        </p>
      </section>

      <div
        v-if="isPending"
        class="text-neutral-500"
      >
        Loading…
      </div>
      <div
        v-else-if="isError"
        class="rounded-md bg-semantic-danger/10 p-4 text-semantic-danger"
      >
        Failed to load: {{ error?.message }}
      </div>
      <div
        v-else
        class="grid gap-4"
      >
        <div class="flex flex-wrap items-center gap-4">
          <input
            v-model="search"
            type="search"
            placeholder="Search ref, name, type or area…"
            class="grow rounded-md border border-neutral-300 px-3 py-2 text-sm"
          >
          <span class="text-sm text-neutral-400">
            {{ (locations ?? []).length }} structures
          </span>
        </div>

        <div class="overflow-hidden rounded-xl border border-neutral-200">
          <table class="w-full text-left text-sm">
            <thead class="bg-neutral-50 text-neutral-500">
              <tr>
                <th class="px-4 py-2 font-medium">
                  Ref
                </th>
                <th class="px-4 py-2 font-medium">
                  Name
                </th>
                <th class="px-4 py-2 font-medium">
                  Type
                </th>
                <th class="px-4 py-2 font-medium">
                  Area
                </th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="l in filtered"
                :key="l.id"
                class="border-t border-neutral-100"
              >
                <td class="px-4 py-2 font-medium text-neutral-900">
                  {{ l.ref }}
                </td>
                <td class="px-4 py-2 text-neutral-500">
                  {{ l.name ?? "—" }}
                </td>
                <td class="px-4 py-2 text-neutral-500">
                  {{ l.structure_type ?? "—" }}
                </td>
                <td class="px-4 py-2 text-neutral-500">
                  {{ l.area ?? "—" }}
                </td>
              </tr>
              <tr v-if="!filtered.length">
                <td
                  colspan="4"
                  class="px-4 py-6 text-center text-neutral-400"
                >
                  No structures yet — import the project's location schedule.
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  </PageLayout>
</template>
